import { useEffect, useRef } from 'react';
import { onHubEvent, type HubEvent } from './realtime';

/**
 * Vuelve a cargar la pantalla cuando llega un aviso que le incumbe.
 *
 *   useLive(['followup', 'patient'], load);
 *
 * Dos cuidados que importan:
 *
 * 1. **Agrupa ráfagas.** Si tres asesoras registran seguimientos en el mismo
 *    segundo llegan tres avisos; recargar tres veces sería peor que el polling
 *    que estamos quitando. Se espera un momento y se recarga UNA vez.
 *
 * 2. **`reload` se lee de una ref.** Las pantallas redefinen su función de
 *    carga en cada render; si dependiéramos de ella, la suscripción se
 *    desmontaría y remontaría constantemente (y con ella el socket).
 */
export function useLive(types: HubEvent['type'][], reload: () => void, opts: { filter?: (e: HubEvent) => boolean; delayMs?: number } = {}): void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const filterRef = useRef(opts.filter);
  filterRef.current = opts.filter;

  const key = types.join(',');
  const delay = opts.delayMs ?? 400;

  useEffect(() => {
    const wanted = new Set(key.split(','));
    let timer: ReturnType<typeof setTimeout> | null = null;

    const off = onHubEvent((e) => {
      if (!wanted.has(e.type)) return;
      if (filterRef.current && !filterRef.current(e)) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; reloadRef.current(); }, delay);
    });

    return () => { if (timer) clearTimeout(timer); off(); };
  }, [key, delay]);
}
